import {
  memo,
  useEffect,
  useLayoutEffect,
  useRef,
  type ReactNode,
  type CSSProperties,
} from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import { areEqualFields } from 'utils/form';
import { isNil, get } from 'lodash';
import useSize from 'hooks/useSize';
import type { TFieldComponentProps } from './types';
import s from './TextArea.module.scss';

// The textarea is controlled: the value is a string, or undefined until the effect sets ''.
export interface ITextAreaProps extends TFieldComponentProps<string | undefined> {
  description?: ReactNode;
  minRows?: number;
  style?: CSSProperties;
  label?: ReactNode;
}

const TextArea = ({
  field,
  form: { touched, errors, setFieldValue },
  description,
  minRows = 4,
  style,
  label,
}: ITextAreaProps) => {
  const labelRef = useRef<HTMLLabelElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const fieldWrapperRef = useRef<HTMLDivElement>(null);
  const { value, name } = field;
  const textSize = useSize(textRef);
  const textHeight = get(textSize, 'height', 0);

  useEffect(() => {
    if (isNil(value)) setFieldValue(name, '');
  }, [name, setFieldValue, value]);

  useLayoutEffect(() => {
    if (labelRef?.current && textRef?.current) {
      const { current } = labelRef;
      if (!value) {
        current.style.top = `${textHeight || 0}px`;
      } else {
        current.style.top = `-40px`;
      }
    }
  }, [textHeight, value]);

  useLayoutEffect(() => {
    if (fieldWrapperRef?.current && textRef?.current) {
      const { current } = fieldWrapperRef;
      current.style.paddingTop = `${textHeight + 10}px`;
    }
  }, [textHeight]);

  return (
    <div className="field-wrapper" style={style} ref={fieldWrapperRef}>
      <div className={s.textareaWrapper}>
        <TextareaAutosize {...field} className={s.textArea} resize="none" minRows={minRows} />
        <label
          className={value ? 'label-when-focused-input' : s.textLabel}
          htmlFor={name}
          ref={labelRef}
        >
          <span ref={textRef}>{label}</span>
        </label>
      </div>
      {description ? (
        <div>
          <small className="muted">{description}</small>
        </div>
      ) : null}
      {errors[name] && touched[name] && <div className="error-text">{errors[name]}</div>}
    </div>
  );
};

export default memo(TextArea, areEqualFields);
